import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { PainelOrcamentoService } from 'painel-orcamento';
import { BehaviorSubject, map, Subject, tap } from 'rxjs';
import { ModeloOrcamento } from './modais/modal-impressao-orcamento/modal-impressao-orcamento.component';
import { SeguradoraResultFilter } from './resultados';
import { CalculoImpressaoOrcamento } from './modais/modal-impressao-orcamento/lista-calculos/lista-calculos';
import { IntegracaoConstants } from 'src/app/constants/integracao';

@Injectable({
  providedIn: 'root'
})
export class ResultadosService {

  public readonly modelosOrcamento: ModeloOrcamento[] = [
    {
      id: 0,
      name: "Tabela Horizontal",
      previewSrc: 'assets/img/orcamento/horizontal.png',
      isHorizontal: true,
      ramos: [IntegracaoConstants.ramos.AUTO],
      gaEvent: "ID_ORC_TABELA_HORIZONTAL",
    },
    {
      id: 1,
      name: "Compacto",
      previewSrc: 'assets/img/orcamento/compacto.png',
      isHorizontal: false,
      ramos: [IntegracaoConstants.ramos.AUTO],
      gaEvent: "ID_ORC_COMPACTO",
    },
    {
      id: 2,
      name: "Tabela Vertical",
      previewSrc: 'assets/img/orcamento/vertical.png',
      isHorizontal: false,
      gaEvent: "ID_ORC_TABELA_VERTICAL",
    },
    {
      id: 3,
      name: "Resumido",
      previewSrc: 'assets/img/orcamento/resumido.png',
      isHorizontal: true,
      gaEvent: "ID_ORC_RESUMIDO",
    },
    {
      id: 4,
      name: "Parcelamento",
      previewSrc: 'assets/img/orcamento/parcelamento.png',
      isHorizontal: false,
      gaEvent: "ID_ORC_PARCELAMENTO",
    },
    {
      id: 5,
      name: "Completo Horizontal",
      previewSrc: 'assets/img/orcamento/padrao.png',
      isHorizontal: false,
      ramos: [IntegracaoConstants.ramos.AUTO, IntegracaoConstants.ramos.RESIDENCIAL, IntegracaoConstants.ramos.CONDOMINIO, IntegracaoConstants.ramos.EMPRESARIAL],
      gaEvent: "ID_ORC_COMPLETO_HORIZONTAL",
    }
  ];

  public _ramos = IntegracaoConstants.ramos;

  orcamentos = signal(null); //Propriedade que lida com orçamentos salvos
  orcamentoResultados = signal(null);
  orcamentoCurrentSegurado = signal(null);
  orcamentoModal = signal(null);
  informacoesCalculo = signal(null);
  resultadoCalculo = signal(null);
  incluirCalculo = signal(null);
  loaderImprimirPdf = signal(false);
  loaderFecharOrcamento = signal(false);
  loaderCopiaOrcamento = signal(false);
  loaderResults = signal(true);
  currentVersionIndex = signal<number>(null);
  apagaOrcamento = signal(null);
  editModeSignal = signal<boolean>(false);
  versionDisabled = signal<boolean>(false);
  orcamentoAtual = signal(null);
  continuaOrcamento = signal(null);
  criaOrcamento = signal(null);
  imprimeOrcamentoSalvo = signal(null);
  modeloSelecionado = signal<ModeloOrcamento>(null);
  observacoesGeraisOrcamento = signal('');
  setaOrcamentoNoPainel = signal(null);
  orcamentoSalvoModal = signal(null);
  copiarOrcamento = signal(null);
  seguradorasFilterList = signal<SeguradoraResultFilter[]>([]);
  atualizaCalculosOrcamento = signal<boolean>(false);
  resultTipo = signal<string>('main');
  setIndexTab = signal<number>(null);
  loaderScreenShot = signal<boolean>(false);
  imprimirOrcamentoHome = signal<boolean>(false);
  indexVersionSelected = signal(null);
  initVersaoFavorita = signal(true);
  isVersaoProvisoria = signal<boolean>(false);
  versionChanged = signal<boolean>(false);
  versionMigrated = signal<boolean>(false);
  public isCotacaoExcedeu30DiasSignal = signal(false);
  imprimirBtnIsReady = signal(false);
  alterarFranquiaBtnIsReady = signal(false);

  destroyRef = inject(DestroyRef);

  constructor(
    private painelOrcamentoService: PainelOrcamentoService
  ) {
    this.modeloSelecionado.set(this.modelosOrcamento[0]);
  }
  
  private subjects = {
    idIntegracao: new BehaviorSubject<any>(null),
    versions: new BehaviorSubject<any>(null),
    currentVersion: new BehaviorSubject<any>(null),
    calculos: new BehaviorSubject<any>(null),
    ramo: new BehaviorSubject<any>(null),
    produtos: new BehaviorSubject<any>(null),
    atualizarCalculo: new BehaviorSubject<any>(null),
    draft: new BehaviorSubject<any>(null),
    recalcularEvent: new BehaviorSubject<boolean>(null),
    negocio: new BehaviorSubject<any>(null)
  }

  streams = {
    idIntegracao$: this.subjects.idIntegracao.asObservable(),
    versions$: this.subjects.versions.asObservable(),
    currentVersion$: this.subjects.currentVersion.asObservable(),
    calculos$: this.subjects.calculos.asObservable(),
    ramo$: this.subjects.ramo.asObservable(),
    produtos$: this.subjects.produtos.asObservable(),
    atualizarCalculo$: this.subjects.atualizarCalculo.asObservable(),
    draft$: this.subjects.draft.asObservable(),
    recalcularEvent$: this.subjects.recalcularEvent.asObservable(),
    negocio$: this.subjects.negocio.asObservable()
  }

  public chamarCotacaoInitialize$ = new Subject<void>();

  // Vamos manter um rastreio dos pacotes que estão expandidos na tela de resultados porque precisamos, toda vez que um novo pacote tenha retornado a prop de "retorno" true, e a lista de pacotes seja atualizada, manter aqueles pacotes expandidos ainda expandidos. 
  // ? Cenário ideal: Somente atualizar aqueles pacotes que de fato foram atualizados, não a lista inteira. 
  public pacotesExpandidos = [];

  updateProdutos = (produtos) => {
    this.subjects.produtos.next(produtos);
  }

  updateIdIntegracao = (idIntegracao: any) => {
    this.subjects.idIntegracao.next(idIntegracao);
  }

  updateNegocio = (negocio: any) => {
    this.subjects.negocio.next(negocio);
  }

  updateVersions = (versions: any) => {
    this.subjects.versions.next(versions);
  }

  updateCurrentVersion = (versionsData) => {
    this.subjects.currentVersion.next(versionsData);
  }

  updateDraft = (draft) => {
    this.subjects.draft.next(draft);
  }

  updateCalculos = (calculos) => {
    this.subjects.calculos.next(calculos);
  }

  updateRamo = (ramo: any) => {
    this.subjects.ramo.next(ramo);
  }

  updateAtualizaCalc = (atualizarCalculo: any) => {
    this.subjects.atualizarCalculo.next(atualizarCalculo);
  }

  updateRecalcularEvent = (value: boolean) => {
    this.subjects.recalcularEvent.next(value);
  }

  buscaOrcamentos = async () => {
    try {
      let orcamentosAtualizados: any = await this.painelOrcamentoService.getOrcamentos();
      if (orcamentosAtualizados.orcamentos.length > 0) {
        orcamentosAtualizados.orcamentos = orcamentosAtualizados.orcamentos.sort((a, b) => {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);

          return dateA.getTime() - dateB.getTime();
        });

        orcamentosAtualizados.orcamentos = orcamentosAtualizados.orcamentos.map((orcamento) => ({...orcamento, template: orcamento.template ?? 0}));
      }
      
      this.orcamentos.set(orcamentosAtualizados);

      return true;
    } catch (error) {
      console.error('Erro ao buscar orçamentos', error);
      return false;
    }

  }

  criarNovoOrcamento = async () => {
    let versions = this.subjects.versions.getValue();
    let obj = { versoes: versions, orcamentos: this.orcamentos() }
    this.criaOrcamento.set(obj);
  }


  gerarPdfPersonalizado = async (calculos, template, orcamentoId?, observacoes?, imprimir?, fechar = false, perfilSelecionado = 'Resumido') => {
    if (fechar) this.loaderFecharOrcamento.set(true);
    if (imprimir) this.loaderImprimirPdf.set(true);
    let obj = {calculos: calculos, template: template, orcamento: orcamentoId, observacoes: observacoes, imprimir: imprimir, fechar: fechar, perfilSelecionado: perfilSelecionado}
    this.continuaOrcamento.set(obj);
  }

  copiaOrcamento = () => {
    this.copiarOrcamento.set(true);
  }

  selecionaCodigo(op, ops) {
    if (ops)
      return ops.find(os => os.viewValue == op)
    else
      return false
  }

  tratamentoOpcao(key, op, ops) {
    switch (key) {
      case "tipoFranquia":
        let codigo = this.selecionaCodigo(op, ops)
        return codigo ? codigo.value : false

      default:
        return op
    }
  }

  limpaVariaveis() {
    this.orcamentoResultados.set(null);
    this.informacoesCalculo.set(null);
    this.resultadoCalculo.set(null);
    this.incluirCalculo.set(null);
    this.currentVersionIndex.set(null);
    this.apagaOrcamento.set(null);
    this.editModeSignal.set(false);
    this.versionDisabled.set(false);
    this.orcamentoCurrentSegurado.set(null);
    this.orcamentoAtual.set(null);
    this.continuaOrcamento.set(null);
    this.criaOrcamento.set(null);
    this.imprimeOrcamentoSalvo.set(null);
    this.observacoesGeraisOrcamento.set('');
    this.setaOrcamentoNoPainel.set(null);
    this.orcamentoSalvoModal.set(null);
    this.copiarOrcamento.set(null);
    this.atualizaCalculosOrcamento.set(false);
    this.imprimirOrcamentoHome.set(false);
    this.indexVersionSelected.set(null);
    this.updateCurrentVersion(null);
    this.pacotesExpandidos = [];
  }

  setaValorFormGerais(calculo, ops, form, coberturasArray?) {
    try{
     Object.keys(form.controls).forEach(key => {
     
        let val = calculo?.informacoesEditadas ? calculo?.informacoesEditadas[key] : null

        if (!val)
           val = calculo ? calculo[key] : null

        if (!val)
          val = calculo?.coberturas[key] ?? null

         if(!val && key == "isGuardaVeiculoValor")
          val = calculo.coberturas.isGuardaVeiculo.valor ?? null        
        
        switch(key){
          case 'despesasExtra':
          case 'protecaoPneuRodas':
          case 'carroReservaAr':
          case 'martelinhoOuro':
          case 'higienizacao':
          case 'farolRetrovis':
          case 'tetoSolar':
          case 'lantFarolAux':
          case 'fumante':
          case 'reparoRapido':
            form.controls[key].setValue({
              "value": typeof val === 'string' && val === "Sim" || (typeof val === 'boolean' && val) ?  true : false,
              "viewValue": typeof val === 'string' && val === "Sim" || (typeof val === 'boolean' && val) ? "Sim" : "Não"
            });
            break
          default:
            form.controls[key].setValue(val)
        }          
      });
    }catch(err){
    }
  }

  public handleIsMelhorEscolha = (calculo: CalculoImpressaoOrcamento) => {
    if (calculo.isMelhorEscolha) {
      calculo.isMelhorEscolha = false;
    } else {
      this.orcamentoModal().forEach(c => {
        c.isMelhorEscolha = false;
      });
      calculo.isMelhorEscolha = true;
    }
  }

  public esconderFranquia(ramo, dadosGerais = false) {
    const ramosComFranquia = [this._ramos.AUTO];

    if(!dadosGerais) ramosComFranquia.push(this._ramos.CELULAR)

    return !ramosComFranquia.includes(ramo);
  }
}
